import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';

// Point to the worker we copied into /public so CRA can serve it
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

/**
 * Renders the first page of a PDF as a thumbnail.
 *
 * Strategy:
 *   1. Try the server-side pre-generated thumbnail at <artifactUrl>/thumbnail.
 *      If the server already has it stored, this is a tiny JPEG — instant.
 *   2. Fall back to client-side pdfjs rendering if the thumbnail endpoint
 *      returns a non-2xx response (e.g. for very old artifacts not yet backfilled).
 *
 * Props:
 *   artifactUrl  – URL of the PDF artifact (no auth header needed; endpoint is public)
 *   width        – desired rendered width in px (height scales proportionally)
 *   onClick      – click handler
 *   style        – extra styles for the outer container
 */
const PdfThumbnail = ({ artifactUrl, width = 120, onClick, style = {} }) => {
  const canvasRef = useRef(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  // Non-null when the server thumbnail was fetched successfully; holds a blob: URL
  const [thumbImgSrc, setThumbImgSrc] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const render = async () => {
      // ── Step 1: try the pre-generated server thumbnail ──────────────────────
      try {
        const thumbResponse = await fetch(`${artifactUrl}/thumbnail`);
        if (thumbResponse.ok) {
          const blob = await thumbResponse.blob();
          if (!cancelled) {
            setThumbImgSrc(URL.createObjectURL(blob));
            setLoading(false);
          }
          return; // done — no need for pdfjs
        }
        // Non-2xx (e.g. 404 for old artifact not yet backfilled) → fall through
      } catch (_) {
        // Network error or endpoint not available → fall through
      }

      // ── Step 2: client-side pdfjs fallback ──────────────────────────────────
      try {
        const response = await fetch(artifactUrl);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const buffer = await response.arrayBuffer();

        const loadingTask = pdfjsLib.getDocument({ data: buffer });
        const pdf = await loadingTask.promise;
        if (cancelled) return;

        const page = await pdf.getPage(1);
        if (cancelled) return;

        const viewport = page.getViewport({ scale: 1 });
        const scale = width / viewport.width;
        const scaled = page.getViewport({ scale });

        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = scaled.width;
        canvas.height = scaled.height;

        await page.render({
          canvasContext: canvas.getContext('2d'),
          viewport: scaled,
        }).promise;

        if (!cancelled) setLoading(false);
      } catch (err) {
        if (!cancelled) {
          console.warn('PdfThumbnail render failed:', err);
          setError(true);
          setLoading(false);
        }
      }
    };

    render();
    return () => { cancelled = true; };
  }, [artifactUrl, width]);

  // Revoke the blob URL when the component unmounts to avoid memory leaks
  useEffect(() => {
    return () => {
      if (thumbImgSrc) URL.revokeObjectURL(thumbImgSrc);
    };
  }, [thumbImgSrc]);

  const containerStyle = {
    width,
    height: width,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    overflow: 'hidden',
    background: '#f9f9f9',
    ...style,
  };

  if (error) {
    // Fallback icon card
    return (
      <div style={containerStyle} onClick={onClick}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 36 }}>📄</span>
          <span style={{
            fontSize: '0.6rem', color: '#c00', fontWeight: 700,
            letterSpacing: '0.05em', background: '#fee2e2',
            padding: '1px 5px', borderRadius: 3,
          }}>PDF</span>
        </div>
      </div>
    );
  }

  // Server returned a pre-generated JPEG thumbnail — fastest path
  if (thumbImgSrc) {
    return (
      <div style={containerStyle} onClick={onClick}>
        <img
          src={thumbImgSrc}
          alt="PDF thumbnail"
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
        />
      </div>
    );
  }

  // pdfjs canvas fallback (used for artifacts not yet backfilled server-side)
  return (
    <div style={containerStyle} onClick={onClick}>
      {loading && (
        <span style={{ fontSize: '0.65rem', color: '#999' }}>Loading…</span>
      )}
      <canvas
        ref={canvasRef}
        style={{
          display: loading ? 'none' : 'block',
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
        }}
      />
    </div>
  );
};

export default PdfThumbnail;
